import { useEffect, useState } from "react";
import axios from "axios";
import { Eye, EyeOff, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/lib/api";

const EMPTY = { title: "", dept: "", type: "Full-time", location: "Tirupati · Hybrid", text: "" };

const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-ink outline-none transition-all duration-300 placeholder:text-gray-400 focus:border-navy focus:ring-4 focus:ring-navy/10";

const JobsPanel = () => {
  const [jobs, setJobs] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () =>
    axios
      .get(`${API}/jobs/manage`, { withCredentials: true })
      .then((r) => setJobs(r.data))
      .catch(() => setJobs([]));

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await axios.patch(`${API}/jobs/${editing}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/jobs`, form, { withCredentials: true });
      }
      toast.success(editing ? "Job updated" : "Job posted");
      setForm(EMPTY);
      setEditing(null);
      setShowForm(false);
      load();
    } catch {
      toast.error("Could not save the job");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (j) => {
    setForm({ title: j.title, dept: j.dept, type: j.type, location: j.location, text: j.text });
    setEditing(j.id);
    setShowForm(true);
  };

  const toggleActive = async (j) => {
    try {
      await axios.patch(
        `${API}/jobs/${j.id}`,
        { title: j.title, dept: j.dept, type: j.type, location: j.location, text: j.text, active: !j.active },
        { withCredentials: true }
      );
      toast.success(j.active ? "Job hidden from Careers page" : "Job is live on Careers page");
      load();
    } catch {
      toast.error("Could not update the job");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this job posting permanently?")) return;
    try {
      await axios.delete(`${API}/jobs/${id}`, { withCredentials: true });
      toast.success("Job deleted");
      load();
    } catch {
      toast.error("Could not delete the job");
    }
  };

  return (
    <div data-testid="jobs-panel">
      <div className="mt-6 flex justify-end">
        <button
          onClick={() => {
            setForm(EMPTY);
            setEditing(null);
            setShowForm((v) => !v);
          }}
          data-testid="add-job-button"
          className="flex items-center gap-2 rounded-full bg-navy px-6 py-3 font-heading text-xs font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-navy-800"
        >
          {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {showForm ? "Close" : "Post a Job"}
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={save}
          data-testid="job-form"
          className="mt-6 rounded-3xl border border-gray-100 bg-white p-8 shadow-sm"
        >
          <h2 className="font-heading text-lg font-bold text-ink">
            {editing ? "Edit job posting" : "New job posting"}
          </h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            <input
              required
              placeholder="Job title"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              data-testid="job-title-input"
              className={`${inputCls} sm:col-span-2`}
            />
            <input
              required
              placeholder="Department (e.g. Engineering)"
              value={form.dept}
              onChange={(e) => setForm((f) => ({ ...f, dept: e.target.value }))}
              data-testid="job-dept-input"
              className={inputCls}
            />
            <input
              required
              placeholder="Type (e.g. Full-time)"
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              data-testid="job-type-input"
              className={inputCls}
            />
            <input
              required
              placeholder="Location"
              value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              data-testid="job-location-input"
              className={inputCls}
            />
            <textarea
              required
              rows={3}
              placeholder="Short description"
              value={form.text}
              onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
              data-testid="job-text-input"
              className={`${inputCls} resize-none sm:col-span-2`}
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="job-save-button"
            className="mt-6 flex items-center gap-2 rounded-full bg-navy px-8 py-3 font-heading text-xs font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-navy-800 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save Changes" : "Publish Job"}
          </button>
        </form>
      )}

      {jobs === null ? (
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-navy" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="mt-10 rounded-3xl border border-gray-100 bg-white py-16 text-center text-sm text-fog">
          No job postings yet. Post your first role.
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {jobs.map((j) => (
            <article
              key={j.id}
              data-testid="admin-job-card"
              className={`rounded-3xl border border-gray-100 bg-white p-7 shadow-sm ${j.active ? "" : "opacity-60"}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h2 className="font-heading text-lg font-bold text-ink">{j.title}</h2>
                    <span
                      className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${
                        j.active ? "bg-emerald-50 text-emerald-600" : "bg-mist text-fog"
                      }`}
                    >
                      {j.active ? "Live" : "Hidden"}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-fog">
                    {j.dept} · {j.type} · {j.location}
                  </p>
                  <p className="mt-2 max-w-2xl text-sm text-fog">{j.text}</p>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  onClick={() => startEdit(j)}
                  data-testid={`job-edit-${j.id}`}
                  className="flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-fog transition-all duration-300 hover:border-royal hover:text-royal"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button
                  onClick={() => toggleActive(j)}
                  data-testid={`job-toggle-${j.id}`}
                  className="flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-fog transition-all duration-300 hover:border-gold hover:text-gold-dark"
                >
                  {j.active ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  {j.active ? "Hide" : "Publish"}
                </button>
                <button
                  onClick={() => remove(j.id)}
                  data-testid={`job-delete-${j.id}`}
                  className="flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-fog transition-all duration-300 hover:border-red-400 hover:text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default JobsPanel;
